import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { kLinkPrivacyPolicyUrl, kLinkTermsOfServiceUrl } from '../../constants/constants-links'
import { PaxtonDisclaimer } from '@/components/disclaimers/PaxtonDisclaimer'
import { AuthContext } from '../../context/auth-context'
import googleAuthRedirect from '../../firebase/auth/google-auth-redirect'
import signInEmail from '../../firebase/auth/sign-in-email'
import { authErrorTranslate } from '../../firebase/auth/auth-error-translate'
import { Button } from '@/components/button/Button'
import { Link } from '@/components/link/Link'
import InlineTipError from '../inline-tips/InlineTipError'
import { FirebaseError } from 'firebase/app'
import * as Sentry from '@sentry/browser'
import { kPaxtonName } from '@/constants/constants-strings'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'

type FormInputs = {
  email: string
  password: string
}

type SignInFormProps = {
  switchToSignUp: () => void
  switchToForgotPassword: () => void
  onSuccess: () => void
  tooltipMessage?: string
  defaultEmail?: string
}

export default function SignInForm(props: SignInFormProps) {
  const { switchToSignUp, switchToForgotPassword, onSuccess, tooltipMessage, defaultEmail } = props

  const authContext = useContext(AuthContext)
  const user = authContext?.user

  // Redirect if the user is not null
  useEffect(() => {
    if (user != null && !user.isAnonymous) {
      onSuccess()
    }
  }, [onSuccess, user])

  // Local State
  const [validationError, setValidationError] = useState('')
  const [loading, setLoading] = useState(false)

  // Form Hook
  const { register, handleSubmit } = useForm<FormInputs>({
    defaultValues: {
      email: defaultEmail || '',
      password: '',
    },
  })

  // Sign in error component
  function ValidationErrorMessage() {
    if (validationError != '') {
      return (
        <div>
          <p className="text-center text-red-700">{validationError}</p>
        </div>
      )
    } else {
      return null
    }
  }

  // Handle Sign Up
  async function handleSignIn(data: FormInputs) {
    setValidationError('')
    setLoading(true)
    try {
      await signInEmail(data.email, data.password)
      // console.log('Signed in user: ', result)

      // onSuccess
      onSuccess()
    } catch (e) {
      if (e instanceof FirebaseError) {
        const validationError = authErrorTranslate(e)
        setValidationError(validationError)
      } else {
        setValidationError(`Unexpected error. ${kPaxtonName} has been notified.`)
      }

      console.warn(e)
      Sentry.captureException(e, {
        extra: {
          email: data.email,
        },
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {tooltipMessage && <div className={'mb-4'}>{InlineTipError(tooltipMessage)}</div>}
      <h2 className="mb-2 text-center text-2xl font-bold tracking-tight text-brand-neutral-900">Sign in</h2>

      <form className="space-y-4" method="POST" onSubmit={handleSubmit((data) => handleSignIn(data))}>
        <div className="mt-2">
          <InputFieldFloatingLabel
            label="Work email address"
            id="email-address"
            required
            registration={register('email', { required: true })}
            disabled={!!defaultEmail}
            showLock={!!defaultEmail}
            inlineMessage={defaultEmail ? 'This invite can only be accepted by the invited email address.' : undefined}
            inputProps={{
              type: 'email',
              autoComplete: 'email',
            }}
            tooltipProps={{
              title: defaultEmail ?? '',
              copyToClipboard: true,
            }}
          />
        </div>
        <div className="mt-2">
          <InputFieldFloatingLabel
            label="Password"
            id="password"
            required
            registration={register('password', { required: true })}
            inputProps={{
              type: 'password',
              autoComplete: 'current-password',
              minLength: 6,
            }}
          />
        </div>

        <div>
          <Button type="submit" fullWidth loading={loading}>
            Sign in
          </Button>
        </div>

        <ValidationErrorMessage />
      </form>

      <p className="mt-5 text-center text-sm text-brand-neutral-500">
        Need an account?{' '}
        <Button variant="plain" size="sm" onClick={switchToSignUp}>
          Sign up
        </Button>
      </p>

      <p className="mt-5 text-center text-sm text-brand-neutral-500">
        Forgot your password?{' '}
        <Button variant="plain" size="sm" onClick={switchToForgotPassword}>
          Send password reset email
        </Button>
      </p>

      <div>
        <div className="relative mt-5">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-sm font-medium leading-6">
            <span className="bg-brand-neutral-50 px-6 text-brand-neutral-900">Or continue with</span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4">
          <button
            onClick={() => googleAuthRedirect()}
            className="flex w-full items-center justify-center gap-3 rounded-md bg-[#ffffff] px-3 py-1.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#24292F] border hover:shadow transition duration-150"
          >
            <img className="w-6 h-6" src="https://www.svgrepo.com/show/475656/google-color.svg" loading="lazy" alt="google logo" />
            <span className="text-sm font-semibold leading-6">Google</span>
          </button>
        </div>
      </div>

      <div className="mt-5 text-xs text-center">
        <Link href={kLinkTermsOfServiceUrl} external size="xs" className="whitespace-nowrap">
          Terms of Service
        </Link>
        &nbsp;&nbsp;&nbsp;
        <Link href={kLinkPrivacyPolicyUrl} external size="xs" className="whitespace-nowrap">
          Privacy Policy
        </Link>
      </div>
      <div className="mt-5 text-xs text-center">
        <PaxtonDisclaimer />
      </div>
    </>
  )
}
